import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Avatar,
  Collapse,
  IconButton,
  useMediaQuery,
  Grid,
  Chip,
  Card,
  CardContent,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
  Tooltip as MuiTooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; 
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import TimelineIcon from '@mui/icons-material/Timeline';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import BarChartIcon from '@mui/icons-material/BarChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RadarIcon from '@mui/icons-material/Assessment';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleIcon from '@mui/icons-material/People';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import SimilarStatsPlayersTab from './SimilarStatsPlayersTab';
import { styled } from '@mui/material/styles';
import { getCompGroup } from '../utils/competitionUtils';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';
  

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-ES').format(date);
};

// Formater les nombres pour ne pas afficher .00
const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';

  // Convertir en nombre, peu importe le format d'entrée
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return '-';

  // Arrondir à 1 décimale
  const rounded = Math.round(num * 10) / 10;

  // Si le nombre arrondi est entier (comme 5.0, 8.0), afficher sans décimales
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }

  // Sinon afficher avec 1 décimale en supprimant le zéro final si présent
  const formatted = rounded.toFixed(1);
  // Supprimer le .0 si présent (ex: 5.0 devient 5)
  return formatted.replace(/\.0$/, '');
};

// Mapeo de estadísticas para el radar
const statMapping = {
  points: 'Puntos',
  assists: 'Asistencias',
  total_rebounds: 'Rebotes',
  steals: 'Robos',
  blocks: 'Tapones',
  turnovers: 'Pérdidas',
  two_fg_percent: 'Tiros 2 %',
  three_fg_percent: 'Tiros 3 %',
  free_throw_percent: 'TL %',
  minutes: 'Minutos',
  ranking: 'Valoración'
};

// Posiciones disponibles
const positions = ['Base', 'Escolta', 'Alero', 'Ala-Pívot', 'Pívot'];

function groupStatsByCompetitionAndSeason(stats) {
  const {
    seasonStats = [],
    matchDetails = [],
    per40 = [],
    percentageDefense = [],
    percentageOffense = []
  } = stats;
  const grouped = {};
  const insertItem = (item, type) => {
    const comboKey = `${item.competition_id}-${item.season_id}`;
    if (!grouped[comboKey]) {
      grouped[comboKey] = {
        competitionName: item.competition_name || `Comp. ${item.competition_id}`,
        seasonYear: item.season_year || `Temp. ${item.season_id}`,
        competitionId: item.competition_id,
        seasonId: item.season_id,
        seasonStats: [],
        matchDetails: [],
        per40: [],
        percentageDefense: [],
        percentageOffense: []
      };
    }
    grouped[comboKey][type].push(item);
  };
  seasonStats.forEach((i) => insertItem(i, 'seasonStats'));
  matchDetails.forEach((i) => insertItem(i, 'matchDetails'));
  per40.forEach((i) => insertItem(i, 'per40'));
  percentageDefense.forEach((i) => insertItem(i, 'percentageDefense'));
  percentageOffense.forEach((i) => insertItem(i, 'percentageOffense'));
  return grouped;
}

const useResponsiveTableStyles = (theme) => ({
  [theme.breakpoints.down('sm')]: {
    '& table': { borderCollapse: 'collapse' },
    '& thead': { display: 'none' },
    '& tbody, & tr, & td, & th': { display: 'block', width: '100%' },
    '& tbody tr': { marginBottom: theme.spacing(1), border: '1px solid #ddd', borderRadius: 4 },
    '& td': { padding: theme.spacing(0.5, 1), textAlign: 'left', position: 'relative' },
    '& td::before': { content: 'attr(data-label)', fontWeight: 600, display: 'inline-block', width: '50%' },
    '& td span': { display: 'inline-block', width: '50%', textAlign: 'right' }
  }
});

const headerCellStyle = { fontWeight: 700, backgroundColor: '#1976d2', color: '#fff', padding: '4px' };

// statLabels is now a function that uses t() inside the component

const getRatingColor = (rating) => {
  if (rating === '+++') return '#4caf50';
  if (rating === '++') return '#66bb6a';
  if (rating === '+') return '#81c784';
  if (rating === '-') return '#ffb74d';
  if (rating === '--') return '#ff9800';
  if (rating === '---') return '#f44336';
  return '#9e9e9e';
};

const CompactStatCard = ({ title, value, icon, color = '#1976d2' }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color, fontSize: '1.1rem' }}>
          {value}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const EstadisticasTab = ({ playerId }) => {
  const { t } = useTranslation();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    seasonStats: [],
    matchDetails: [],
    per40: [],
    percentageDefense: [],
    percentageOffense: []
  });
  const [strengthsWeaknesses, setStrengthsWeaknesses] = useState({
    strengths: [],
    toImprove: [],
    weaknesses: []
  });
  const [careerStats, setCareerStats] = useState(null);
  const [tabValues, setTabValues] = useState({});
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [radarSeasonType, setRadarSeasonType] = useState('current');
  const [playerInfo, setPlayerInfo] = useState(null);

  // États pour la nouvelle table unifiée
  const [showDetails, setShowDetails] = useState(false);
  const [displayMode, setDisplayMode] = useState('average'); // 'average', 'total', 'per40', 'percentage', 'games'

  // États pour le radar
  const [radarLoading, setRadarLoading] = useState(false);
  const [radarComparisonData, setRadarComparisonData] = useState(null);
  const [radarData, setRadarData] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [includeAgeFilter, setIncludeAgeFilter] = useState(false);
  const [ageRange, setAgeRange] = useState(2);
  const [mainTab, setMainTab] = useState(0);

  // Projection inter-ligas state
  const [eurobasketComps, setEurobasketComps] = useState([]);
  const [destinationComp, setDestinationComp] = useState(() => {
    try {
      const saved = localStorage.getItem('projection_destination_comp');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [projectionOpen, setProjectionOpen] = useState({});
  const [projectionResults, setProjectionResults] = useState({});
  const [projectionLoading, setProjectionLoading] = useState({});

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const tableResponsiveStyles = useResponsiveTableStyles(theme);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [statsRes, strengthsRes, careerRes, playerRes] = await Promise.all([
          api.get(`/api/player-stats/${playerId}`),
          api.get(`/api/player-stats/${playerId}/strengths-weaknesses`),
          api.get(`/api/player-stats/${playerId}/career`),
          api.get(`/api/players/${playerId}`)
        ]);
        setStats(statsRes.data);
        
        // Séparer les weaknesses en deux catégories basées sur le rating
        const processedData = {
          strengths: strengthsRes.data.strengths || [],
          toImprove: [],
          weaknesses: []
        };

        // Diviser les faiblesses entre "à améliorer" (rating '-' ou '--') et "débiles" (rating '---')
        // Exclure les pourcentages de tirs à 0% (qui indiquent qu'aucun tir n'a été pris)
        if (strengthsRes.data.weaknesses) {
          strengthsRes.data.weaknesses.forEach(item => {
            // Vérifier si c'est un pourcentage de tir à 0% (aucun tir pris)
            const isShootingPercent = ['two_fg_percent', 'three_fg_percent', 'free_throw_percent'].includes(item.stat);
            const isZeroPercent = item.value === 0;

            // Ne pas inclure les pourcentages de tir à 0% dans les faiblesses
            if (isShootingPercent && isZeroPercent) {
              return; // Ignorer cette statistique
            }

            if (item.rating === '---') {
              processedData.weaknesses.push(item);
            } else {
              processedData.toImprove.push(item);
            }
          });
        }
        
        setStrengthsWeaknesses(processedData);
        setCareerStats(careerRes.data);

        // Guardar información del jugador
        const firstName = playerRes.data.prenom || '';
        const lastName = playerRes.data.nom || '';
        const fullName = `${firstName} ${lastName}`.trim();

        setPlayerInfo({
          id: playerRes.data.id,
          name: fullName || 'Jugador',
          position: playerRes.data.poste,
          age: playerRes.data.age || calculateAge(playerRes.data.date_naissance)
        });
      } catch (e) {
        console.error(e);
        setError(t('estadisticasTab.errorLoading'));
      } finally {
        setIsLoading(false);
      }
    };
    if (playerId) fetchData();
  }, [playerId, backendUrl, section]);

  // Fetch eurobasket competitions for projection (once)
  useEffect(() => {
    api.get('/api/comp/api-competitions', { params: { source: 'eurobasket' } })
      .then(res => setEurobasketComps(res.data || []))
      .catch(() => {});
  }, []);

  // Persist destination comp in localStorage
  useEffect(() => {
    if (destinationComp) {
      localStorage.setItem('projection_destination_comp', JSON.stringify(destinationComp));
    }
  }, [destinationComp]);

  // Reset open projections when destination changes
  useEffect(() => {
    setProjectionOpen({});
    setProjectionResults({});
  }, [destinationComp]);

  const LABEL_TO_KEY = {
    'Pts': 'points', 'Min': 'minutes', 'RDef': 'rebounds_def',
    'ROff': 'rebounds_off', 'RB': 'total_rebounds', 'Ast': 'assists',
    'Stl': 'steals', 'TOv': 'turnovers', 'BLK': 'blocks',
    'PFM': 'personal_fouls', 'Eff': 'ranking'
  };

  const FACTOR_TO_STAT = {
    'points': 'Points', 'minutes': 'Minutes', 'rebounds_def': 'ReboundsDef',
    'rebounds_off': 'ReboundsOff', 'total_rebounds': 'Rebounds', 'assists': 'Assists',
    'steals': 'Steals', 'turnovers': 'Turnovers', 'blocks': 'Blocks',
    'personal_fouls': 'Fouls', 'ranking': 'Ranking'
  };

  const toggleProjection = async (compName, seasonYear) => {
    const key = `${compName}-${seasonYear}`;
    if (projectionOpen[key]) {
      setProjectionOpen(prev => ({ ...prev, [key]: false }));
      return;
    }
    if (!destinationComp) return;

    setProjectionLoading(prev => ({ ...prev, [key]: true }));
    try {
      const resolveRes = await api.get('/api/comp/resolve-competition', { params: { name: compName } });
      if (!resolveRes.data.found) return;

      const sourceId = resolveRes.data.id;
      if (sourceId === destinationComp.id) return; // same league

      const cacheKey = `${sourceId}→${destinationComp.id}`;
      let factorData = projectionResults[key];

      if (!factorData) {
        const perfRes = await api.post('/api/comp/performance', {
          origin_competition_id: sourceId,
          destination_competition_id: destinationComp.id,
          source: 'eurobasket'
        });
        if (!perfRes.data || perfRes.data.sample_size === 0) {
          setProjectionResults(prev => ({ ...prev, [key]: { noData: true, destName: destinationComp.name } }));
          setProjectionOpen(prev => ({ ...prev, [key]: true }));
          return;
        }

        const factors = {};
        perfRes.data.stats?.forEach(s => {
          const k = LABEL_TO_KEY[s.name];
          if (k) factors[k] = s.factor;
        });

        factorData = {
          factors,
          sample_size: perfRes.data.sample_size,
          unique_players: perfRes.data.unique_players,
          destName: destinationComp.name
        };
        setProjectionResults(prev => ({ ...prev, [key]: factorData }));
      }

      setProjectionOpen(prev => ({ ...prev, [key]: true }));
    } catch (err) {
      console.error('Projection error:', err);
    } finally {
      setProjectionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const applyProjection = (compStats, factors) => {
    if (!compStats || !factors) return null;
    const projected = { ...compStats };
    Object.entries(factors).forEach(([factorKey, factor]) => {
      const statSuffix = FACTOR_TO_STAT[factorKey];
      if (!statSuffix) return;
      ['avg', 'total', 'per40'].forEach(prefix => {
        const k = `${prefix}${statSuffix}`;
        if (projected[k] != null) projected[k] = projected[k] * (1 + factor / 100);
      });
    });
    if (factors.minutes != null && projected.avgMinutes != null) {
      projected.avgMinutes = projected.avgMinutes * (1 + factors.minutes / 100);
    }
    projected.totalGames = null;
    // Shooting %: no eurobasket factors → keep null
    projected.fgPercent = null;
    projected.threePPercent = null;
    projected.ftPercent = null;
    return projected;
  };

  // Función auxiliar para calcular edad
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to get translated stat labels
  const getStatLabel = (stat) => {
    return t(`estadisticasTab.statLabels.${stat}`, stat);
  };

  // Fonction pour changer le type de saison du radar
  const handleRadarSeasonTypeChange = (type) => {
    setRadarSeasonType(type);
  };

  // Initialiser avec la position du joueur
  useEffect(() => {
    if (playerInfo?.position) {
      setSelectedPositions([playerInfo.position]);
    }
  }, [playerInfo]);

  // Fetcher les données de comparaison pour le radar
  useEffect(() => {
    if (selectedPositions.length > 0 && playerInfo) {
      fetchRadarComparisonData();
    }
  }, [selectedPositions, includeAgeFilter, ageRange, radarSeasonType, playerInfo]);

  const fetchRadarComparisonData = async () => {
    try {
      setRadarLoading(true);

      const params = {
        playerId,
        positions: selectedPositions.join(','),
        section,
        seasonType: radarSeasonType
      };

      if (includeAgeFilter && playerInfo?.age) {
        params.minAge = playerInfo.age - ageRange;
        params.maxAge = playerInfo.age + ageRange;
      }

      const response = await api.get('/api/player-stats/comparison', { params });

      setRadarComparisonData(response.data);
      prepareRadarData(response.data);

    } catch (err) {
      console.error('Error fetching comparison data:', err);
      const mockData = generateMockComparisonData(selectedPositions, includeAgeFilter ? playerInfo?.age : null, ageRange);
      setRadarComparisonData(mockData);
      prepareRadarData(mockData);
    } finally {
      setRadarLoading(false);
    }
  };

  const generateMockComparisonData = (positions, age, range) => {
    const positionAverages = {
      'Base': { points: 11.2, assists: 4.8, total_rebounds: 2.5, steals: 1.2, blocks: 0.1, turnovers: 1.8, two_fg_percent: 48, three_fg_percent: 36, free_throw_percent: 82, minutes: 24.5, ranking: 11.5 },
      'Escolta': { points: 12.5, assists: 2.2, total_rebounds: 3.1, steals: 0.9, blocks: 0.2, turnovers: 1.5, two_fg_percent: 46, three_fg_percent: 38, free_throw_percent: 85, minutes: 25.8, ranking: 12.8 },
      'Alero': { points: 10.8, assists: 2.5, total_rebounds: 4.2, steals: 0.8, blocks: 0.4, turnovers: 1.4, two_fg_percent: 49, three_fg_percent: 35, free_throw_percent: 78, minutes: 26.2, ranking: 13.2 },
      'Ala-Pívot': { points: 9.5, assists: 1.8, total_rebounds: 5.5, steals: 0.7, blocks: 0.6, turnovers: 1.3, two_fg_percent: 52, three_fg_percent: 32, free_throw_percent: 72, minutes: 23.1, ranking: 12.1 },
      'Pívot': { points: 8.3, assists: 1.1, total_rebounds: 6.2, steals: 0.5, blocks: 1.0, turnovers: 1.2, two_fg_percent: 58, three_fg_percent: 28, free_throw_percent: 68, minutes: 20.5, ranking: 11.5 }
    };

    let averages = {};
    if (positions.length === 1) {
      averages = positionAverages[positions[0]] || positionAverages['Alero'];
    } else {
      const keys = Object.keys(positionAverages['Base']);
      keys.forEach(key => {
        averages[key] = positions.reduce((sum, pos) => {
          return sum + (positionAverages[pos]?.[key] || 0);
        }, 0) / positions.length;
      });
    }

    if (age) {
      Object.keys(averages).forEach(key => {
        if (age < 23) {
          averages[key] *= 0.9;
        } else if (age > 30) {
          averages[key] *= 0.95;
        }
      });
    }

    return {
      averages,
      playerCount: Math.floor(Math.random() * 50) + 20
    };
  };

  const prepareRadarData = (data) => {
    if (!data) return;

    let playerStats;
    if (radarSeasonType === 'current') {
      playerStats = stats.seasonStats && stats.seasonStats.length > 0 ? stats.seasonStats[0] : null;
    } else if (radarSeasonType === 'career' && careerStats) {
      playerStats = {
        points: careerStats.totals.avg_points || 0,
        assists: careerStats.totals.avg_assists || 0,
        total_rebounds: careerStats.totals.avg_rebounds || 0,
        steals: careerStats.totals.avg_steals || 0,
        blocks: careerStats.totals.avg_blocks || 0,
        turnovers: careerStats.totals.avg_turnovers || 0,
        two_fg_percent: careerStats.totals.avg_two_fg_percent || 0,
        three_fg_percent: careerStats.totals.avg_three_fg_percent || 0,
        free_throw_percent: careerStats.totals.avg_free_throw_percent || 0,
        minutes: careerStats.totals.avg_minutes || 0,
        ranking: careerStats.totals.avg_ranking || 0
      };
    }

    if (!playerStats) {
      setRadarData([]);
      return;
    }

    const radarPoints = [];
    const displayName = playerInfo?.name || 'Jugador';

    const statMaxValues = {
      points: 40,
      assists: 15,
      total_rebounds: 15,
      steals: 5,
      blocks: 5,
      turnovers: 5,
      minutes: 48,
      ranking: 60
    };

    ['points', 'assists', 'total_rebounds', 'steals', 'blocks', 'turnovers', 'minutes', 'ranking'].forEach(key => {
      if (statMapping[key]) {
        const playerValue = parseFloat(playerStats[key]) || 0;
        const avgValue = parseFloat(data.averages?.[key]) || 0;
        const maxValue = statMaxValues[key];

        const normalizedPlayer = (playerValue / maxValue) * 100;
        const normalizedAvg = (avgValue / maxValue) * 100;

        radarPoints.push({
          stat: statMapping[key],
          [displayName]: Math.min(normalizedPlayer, 100),
          'Promedio Liga': Math.min(normalizedAvg, 100),
          realPlayer: playerValue,
          realAvg: avgValue
        });
      }
    });

    ['two_fg_percent', 'three_fg_percent', 'free_throw_percent'].forEach(key => {
      if (statMapping[key]) {
        const playerValue = parseFloat(playerStats[key]) || 0;
        const avgValue = parseFloat(data.averages?.[key]) || 0;

        radarPoints.push({
          stat: statMapping[key],
          [displayName]: playerValue,
          'Promedio Liga': avgValue,
          realPlayer: playerValue,
          realAvg: avgValue
        });
      }
    });

    setRadarData(radarPoints);
  };

  const handlePositionChange = (event) => {
    const value = event.target.value;
    setSelectedPositions(typeof value === 'string' ? value.split(',') : value);
  };

  // Fonctions pour la nouvelle table unifiée
  const groupStatsBySeason = (seasonStats) => {
    const grouped = {};
    seasonStats.forEach(stat => {
      const seasonKey = stat.season_year || 'Unknown';
      if (!grouped[seasonKey]) {
        grouped[seasonKey] = {
          seasonYear: seasonKey,
          competitions: []
        };
      }
      grouped[seasonKey].competitions.push({
        name: stat.competition_name || 'Unknown',
        stats: stat
      });
    });
    return grouped;
  };

  // Calculer les tirs totaux à partir des matchs
  const calculateShootingFromMatches = (competitionName, seasonYear) => {
    const matches = stats.matchDetails.filter(match =>
      match.competition_name === competitionName && match.season_year === seasonYear
    );

    let totalTwoMade = 0;
    let totalTwoAttempted = 0;
    let totalThreeMade = 0;
    let totalThreeAttempted = 0;
    let totalFtMade = 0;
    let totalFtAttempted = 0;

    const parseShotRatio = (value) => {
      if (!value) return { made: 0, attempted: 0 };
      const str = String(value);
      if (str.includes('-')) {
        const [made, attempted] = str.split('-').map(v => parseInt(v.trim()) || 0);
        return { made, attempted };
      }
      return { made: 0, attempted: 0 };
    };

    matches.forEach(match => {
      // Parser les vraies données de tirs depuis les propriétés du match
      // Les données sont au format "made-attempted" (ex: "4-6")
      const twoFg = parseShotRatio(match.two_fg_percent);
      totalTwoMade += twoFg.made;
      totalTwoAttempted += twoFg.attempted;

      const threeFg = parseShotRatio(match.three_fg_percent);
      totalThreeMade += threeFg.made;
      totalThreeAttempted += threeFg.attempted;

      const ft = parseShotRatio(match.free_throw_percent);
      totalFtMade += ft.made;
      totalFtAttempted += ft.attempted;
    });

    return {
      twoMade: totalTwoMade,
      twoAttempted: totalTwoAttempted,
      threeMade: totalThreeMade,
      threeAttempted: totalThreeAttempted,
      ftMade: totalFtMade,
      ftAttempted: totalFtAttempted
    };
  };

  const calculateSeasonStatsUnified = (competitions, seasonYear) => {
    if (!competitions || competitions.length === 0) return null;

    let totals = {
      games: 0,
      totalMinutes: 0,
      totalPoints: 0,
      totalRebounds: 0,
      totalReboundsOff: 0,
      totalReboundsDef: 0,
      totalAssists: 0,
      totalSteals: 0,
      totalBlocks: 0,
      totalTurnovers: 0,
      totalFouls: 0,
      totalRanking: 0,
      totalFgMade: 0,
      totalFgAttempted: 0,
      total3pMade: 0,
      total3pAttempted: 0,
      totalFtMade: 0,
      totalFtAttempted: 0
    };

    competitions.forEach(comp => {
      const s = comp.stats;
      const games = s.games || 0;

      totals.games += games;
      totals.totalMinutes += (s.minutes || 0) * games;
      totals.totalPoints += (s.points || 0) * games;
      totals.totalRebounds += (s.total_rebounds || 0) * games;
      totals.totalReboundsOff += (s.rebounds_off || 0) * games;
      totals.totalReboundsDef += (s.rebounds_def || 0) * games;
      totals.totalAssists += (s.assists || 0) * games;
      totals.totalSteals += (s.steals || 0) * games;
      totals.totalBlocks += (s.blocks || 0) * games;
      totals.totalTurnovers += (s.turnovers || 0) * games;
      totals.totalFouls += (s.personal_fouls || 0) * games;
      totals.totalRanking += (s.ranking || 0) * games;

      // Calculer les tirs à partir des matchs
      const shooting = calculateShootingFromMatches(comp.name, seasonYear);
      totals.totalFgMade += shooting.twoMade;
      totals.totalFgAttempted += shooting.twoAttempted;
      totals.total3pMade += shooting.threeMade;
      totals.total3pAttempted += shooting.threeAttempted;
      totals.totalFtMade += shooting.ftMade;
      totals.totalFtAttempted += shooting.ftAttempted;
    });

    if (totals.games === 0) return null;

    const avgMinutes = totals.totalMinutes / totals.games;
    const per40Factor = avgMinutes > 0 ? 40 / avgMinutes : 0;

    return {
      totalGames: totals.games,
      totalMinutes: totals.totalMinutes,
      totalPoints: totals.totalPoints,
      totalRebounds: totals.totalRebounds,
      totalReboundsOff: totals.totalReboundsOff,
      totalReboundsDef: totals.totalReboundsDef,
      totalAssists: totals.totalAssists,
      totalSteals: totals.totalSteals,
      totalBlocks: totals.totalBlocks,
      totalTurnovers: totals.totalTurnovers,
      totalFouls: totals.totalFouls,
      totalRanking: totals.totalRanking,
      avgMinutes: avgMinutes,
      avgPoints: totals.totalPoints / totals.games,
      avgRebounds: totals.totalRebounds / totals.games,
      avgReboundsOff: totals.totalReboundsOff / totals.games,
      avgReboundsDef: totals.totalReboundsDef / totals.games,
      avgAssists: totals.totalAssists / totals.games,
      avgSteals: totals.totalSteals / totals.games,
      avgBlocks: totals.totalBlocks / totals.games,
      avgTurnovers: totals.totalTurnovers / totals.games,
      avgFouls: totals.totalFouls / totals.games,
      avgRanking: totals.totalRanking / totals.games,
      per40Minutes: 40,
      per40Points: (totals.totalPoints / totals.games) * per40Factor,
      per40Rebounds: (totals.totalRebounds / totals.games) * per40Factor,
      per40ReboundsOff: (totals.totalReboundsOff / totals.games) * per40Factor,
      per40ReboundsDef: (totals.totalReboundsDef / totals.games) * per40Factor,
      per40Assists: (totals.totalAssists / totals.games) * per40Factor,
      per40Steals: (totals.totalSteals / totals.games) * per40Factor,
      per40Blocks: (totals.totalBlocks / totals.games) * per40Factor,
      per40Turnovers: (totals.totalTurnovers / totals.games) * per40Factor,
      per40Fouls: (totals.totalFouls / totals.games) * per40Factor,
      per40Ranking: (totals.totalRanking / totals.games) * per40Factor,
      // Calculer les pourcentages à partir des vrais totaux de tirs
      fgPercent: totals.totalFgAttempted > 0 ? (totals.totalFgMade / totals.totalFgAttempted) * 100 : 0,
      threePPercent: totals.total3pAttempted > 0 ? (totals.total3pMade / totals.total3pAttempted) * 100 : 0,
      ftPercent: totals.totalFtAttempted > 0 ? (totals.totalFtMade / totals.totalFtAttempted) * 100 : 0,
      // Stocker les totaux de tirs
      totalFgMade: totals.totalFgMade,
      totalFgAttempted: totals.totalFgAttempted,
      total3pMade: totals.total3pMade,
      total3pAttempted: totals.total3pAttempted,
      totalFtMade: totals.totalFtMade,
      totalFtAttempted: totals.totalFtAttempted,
      avgFgMade: totals.totalFgMade / totals.games,
      avgFgAttempted: totals.totalFgAttempted / totals.games,
      avg3pMade: totals.total3pMade / totals.games,
      avg3pAttempted: totals.total3pAttempted / totals.games,
      avgFtMade: totals.totalFtMade / totals.games,
      avgFtAttempted: totals.totalFtAttempted / totals.games
    };
  };

  const getCompetitionStatsUnified = (stat, competitionName, seasonYear) => {
    const games = stat.games || 0;
    const avgMinutes = stat.minutes || 0;
    const per40Factor = avgMinutes > 0 ? 40 / avgMinutes : 0;

    // Calculer les tirs à partir des matchs
    const shooting = calculateShootingFromMatches(competitionName, seasonYear);

    return {
      totalGames: games,
      totalMinutes: avgMinutes * games,
      totalPoints: (stat.points || 0) * games,
      totalRebounds: (stat.total_rebounds || 0) * games,
      totalReboundsOff: (stat.rebounds_off || 0) * games,
      totalReboundsDef: (stat.rebounds_def || 0) * games,
      totalAssists: (stat.assists || 0) * games,
      totalSteals: (stat.steals || 0) * games,
      totalBlocks: (stat.blocks || 0) * games,
      totalTurnovers: (stat.turnovers || 0) * games,
      totalFouls: (stat.personal_fouls || 0) * games,
      totalRanking: (stat.ranking || 0) * games,
      avgMinutes: avgMinutes,
      avgPoints: stat.points || 0,
      avgRebounds: stat.total_rebounds || 0,
      avgReboundsOff: stat.rebounds_off || 0,
      avgReboundsDef: stat.rebounds_def || 0,
      avgAssists: stat.assists || 0,
      avgSteals: stat.steals || 0,
      avgBlocks: stat.blocks || 0,
      avgTurnovers: stat.turnovers || 0,
      avgFouls: stat.personal_fouls || 0,
      avgRanking: stat.ranking || 0,
      per40Minutes: 40,
      per40Points: (stat.points || 0) * per40Factor,
      per40Rebounds: (stat.total_rebounds || 0) * per40Factor,
      per40ReboundsOff: (stat.rebounds_off || 0) * per40Factor,
      per40ReboundsDef: (stat.rebounds_def || 0) * per40Factor,
      per40Assists: (stat.assists || 0) * per40Factor,
      per40Steals: (stat.steals || 0) * per40Factor,
      per40Blocks: (stat.blocks || 0) * per40Factor,
      per40Turnovers: (stat.turnovers || 0) * per40Factor,
      per40Fouls: (stat.personal_fouls || 0) * per40Factor,
      per40Ranking: (stat.ranking || 0) * per40Factor,
      // Calculer les pourcentages à partir des vrais totaux de tirs
      fgPercent: shooting.twoAttempted > 0 ? (shooting.twoMade / shooting.twoAttempted) * 100 : 0,
      threePPercent: shooting.threeAttempted > 0 ? (shooting.threeMade / shooting.threeAttempted) * 100 : 0,
      ftPercent: shooting.ftAttempted > 0 ? (shooting.ftMade / shooting.ftAttempted) * 100 : 0,
      // Stocker les totaux de tirs
      totalFgMade: shooting.twoMade,
      totalFgAttempted: shooting.twoAttempted,
      total3pMade: shooting.threeMade,
      total3pAttempted: shooting.threeAttempted,
      totalFtMade: shooting.ftMade,
      totalFtAttempted: shooting.ftAttempted,
      avgFgMade: shooting.twoMade / (games || 1),
      avgFgAttempted: shooting.twoAttempted / (games || 1),
      avg3pMade: shooting.threeMade / (games || 1),
      avg3pAttempted: shooting.threeAttempted / (games || 1),
      avgFtMade: shooting.ftMade / (games || 1),
      avgFtAttempted: shooting.ftAttempted / (games || 1)
    };
  };

  const formatStatValue = (stats, statKey) => {
    if (!stats) return '-';
    let value;
    switch (displayMode) {
      case 'total':
        value = stats[`total${statKey.charAt(0).toUpperCase() + statKey.slice(1)}`];
        // Pour les totaux, arrondir à l'entier
        if (value === null || value === undefined) return '-';
        return Math.round(value).toString();
      case 'per40':
        value = stats[`per40${statKey.charAt(0).toUpperCase() + statKey.slice(1)}`];
        break;
      case 'average':
      default:
        value = stats[`avg${statKey.charAt(0).toUpperCase() + statKey.slice(1)}`];
        break;
    }
    return formatNumber(value);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined || value === 0) return '-';
    return `${formatNumber(value)}%`;
  };

  const formatPercentageWithRatio = (percent, made, attempted) => {
    if (!percent || percent === 0) return '-';
    const madeRounded = Math.round(made || 0);
    const attemptedRounded = Math.round(attempted || 0);
    return `${formatNumber(percent)}% (${madeRounded}/${attemptedRounded})`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isPercentage = data.stat.includes('%');

      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {data.stat}
          </Typography>
          {payload.map((entry, index) => {
            const realValue = entry.name === (playerInfo?.name || 'Jugador')
              ? data.realPlayer
              : data.realAvg;

            return (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: entry.color,
                    borderRadius: '50%'
                  }}
                />
                <Typography variant="body2">
                  {entry.name}: {formatNumber(realValue)}
                  {isPercentage ? '%' : ''}
                </Typography>
              </Box>
            );
          })}
        </Paper>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const grouped = groupStatsByCompetitionAndSeason(stats);

  // Trier les clés : par saison décroissante d'abord, puis par nombre de matchs décroissant
  const groupKeys = Object.keys(grouped).sort((a, b) => {
    const seasonA = grouped[a];
    const seasonB = grouped[b];

    // Extraire l'année de la saison (ex: "2024/2025" -> 2025)
    const getLatestYear = (seasonYear) => {
      if (!seasonYear) return 0;
      const years = seasonYear.toString().match(/\d{4}/g);
      return years ? Math.max(...years.map(Number)) : 0;
    };

    const yearA = getLatestYear(seasonA.seasonYear);
    const yearB = getLatestYear(seasonB.seasonYear);

    // 1. Trier par année décroissante
    if (yearB !== yearA) {
      return yearB - yearA;
    }

    // 2. Si même année, trier par nombre de matchs décroissant
    const gamesA = seasonA.seasonStats[0]?.games || 0;
    const gamesB = seasonB.seasonStats[0]?.games || 0;
    return gamesB - gamesA;
  });

  // Determinar si mostrar uno o dos botones
  const hasMultipleSeasons = groupKeys.length > 1;
  const currentSeasonStats = stats.seasonStats && stats.seasonStats.length > 0 ? stats.seasonStats[0] : null;
  const noData =
    groupKeys.length === 0 ||
    (stats.seasonStats.length === 0 &&
      stats.matchDetails.length === 0 &&
      stats.per40.length === 0 &&
      stats.percentageDefense.length === 0 &&
      stats.percentageOffense.length === 0);

  if (noData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t('estadisticasTab.noData')}
      </Alert>
    );
  }

  const handleTabChange = (seasonKey) => (event, newValue) => {
    setTabValues(prev => ({ ...prev, [seasonKey]: newValue }));
  };

  //  Fonction pour extraire les scores et déterminer la couleur
  const getScoreDisplay = (result) => {
    if (!result || !result.includes('-')) return { display: result || '-', color: 'inherit' };
    
    const [teamScore, opponentScore] = result.split('-').map(s => parseInt(s.trim()));
    const isVictory = teamScore > opponentScore;
    
    return {
      display: result,
      color: isVictory ? '#4caf50' : '#f44336'
    };
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Tabs */}
      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<BarChartIcon />}
          iconPosition="start"
          label={t('estadisticasTab.seasonStats')}
        />
        <Tab
          icon={<PeopleIcon />}
          iconPosition="start"
          label={t('estadisticasTab.similarStats.tabTitle')}
        />
      </Tabs>

      {/* Tab 0: Statistiques */}
      {mainTab === 0 && (
      <>
      {/* ANÁLISIS DEL JUGADOR - VERSION COMPACTE */}
      {(strengthsWeaknesses.strengths.length > 0 || strengthsWeaknesses.toImprove.length > 0 || strengthsWeaknesses.weaknesses.length > 0) && (
        <Paper elevation={0} sx={{ mb: { xs: 1, sm: 1.5 }, p: { xs: 1, sm: 1.5 }, border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              {t('estadisticasTab.playerAnalysis')}
            </Typography>
          </Box>
          <Grid container spacing={1.5}>
            {strengthsWeaknesses.strengths.length > 0 && (
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <TrendingUpIcon sx={{ color: '#4caf50', mr: 0.5, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('estadisticasTab.strengths')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {strengthsWeaknesses.strengths.map((item, index) => (
                    <Chip
                      key={index}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                            {getStatLabel(item.stat)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: getRatingColor(item.rating), fontSize: '1rem', lineHeight: 1 }}>
                            {item.rating}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                            ({formatNumber(item.value)})
                          </Typography>
                        </Box>
                      }
                      sx={{
                        backgroundColor: '#e8f5e9',
                        border: '1px solid #c8e6c9',
                        height: 'auto',
                        '& .MuiChip-label': {
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            {strengthsWeaknesses.toImprove.length > 0 && (
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <RemoveIcon sx={{ color: '#ff9800', mr: 0.5, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('estadisticasTab.toImprove')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {strengthsWeaknesses.toImprove.map((item, index) => (
                    <Chip
                      key={index}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                            {getStatLabel(item.stat)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: getRatingColor(item.rating), fontSize: '1rem', lineHeight: 1 }}>
                            {item.rating}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                            ({formatNumber(item.value)})
                          </Typography>
                        </Box>
                      }
                      sx={{
                        backgroundColor: '#fff3e0',
                        border: '1px solid #ffcc80',
                        height: 'auto',
                        '& .MuiChip-label': {
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            {strengthsWeaknesses.weaknesses.length > 0 && (
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <CloseIcon sx={{ color: '#f44336', mr: 0.5, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('estadisticasTab.weaknesses')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {strengthsWeaknesses.weaknesses.map((item, index) => (
                    <Chip
                      key={index}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                            {getStatLabel(item.stat)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: getRatingColor(item.rating), fontSize: '1rem', lineHeight: 1 }}>
                            {item.rating}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                            ({formatNumber(item.value)})
                          </Typography>
                        </Box>
                      }
                      sx={{
                        backgroundColor: '#ffebee',
                        border: '1px solid #ffcdd2',
                        height: 'auto',
                        '& .MuiChip-label': {
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}


      {/* NOUVELLE TABLE UNIFIÉE DES STATISTIQUES */}
      <Paper elevation={0} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('estadisticasTab.seasonStats')}
        </Typography>

        {/* Boutons de contrôle */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant={showDetails ? 'contained' : 'outlined'}
            size="small"
            startIcon={showDetails ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() => setShowDetails(!showDetails)}
            sx={{ textTransform: 'none' }}
          >
            {t('estadisticasTab.detail')}
          </Button>
        </Box>

        {/* Boutons de mode d'affichage */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant={displayMode === 'average' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setDisplayMode('average')}
            sx={{ textTransform: 'none' }}
          >
            {t('estadisticasTab.average')}
          </Button>
          <Button
            variant={displayMode === 'total' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setDisplayMode('total')}
            sx={{ textTransform: 'none' }}
          >
            {t('estadisticasTab.total')}
          </Button>
          <Button
            variant={displayMode === 'per40' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setDisplayMode('per40')}
            sx={{ textTransform: 'none' }}
          >
            {t('estadisticasTab.per40')}
          </Button>
          <Button
            variant={displayMode === 'games' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setDisplayMode('games')}
            sx={{ textTransform: 'none' }}
          >
            {t('estadisticasTab.games')}
          </Button>
        </Box>

        {/* Projection destination selector */}
        {displayMode !== 'games' && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t('estadisticasTab.projectionDestination')}:
            </Typography>
            <Autocomplete
              options={eurobasketComps}
              groupBy={(opt) => getCompGroup(opt.name)}
              getOptionLabel={(opt) => `${opt.name} (${opt.nb_players})`}
              value={destinationComp}
              onChange={(e, v) => setDestinationComp(v)}
              renderInput={(params) => (
                <TextField {...params} placeholder={t('estadisticasTab.selectLeague')} size="small" />
              )}
              sx={{ minWidth: 280 }}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
          </Box>
        )}

        {/* Table des statistiques - Moyenne/Total/Por 40 min */}
        {(displayMode === 'average' || displayMode === 'total' || displayMode === 'per40') && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 150 }}>
                    {t('estadisticasTab.season')}
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.part')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.min')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.pts')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.t2')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.t3')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.tl')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.rebO')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.rebD')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.rebT')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.ast')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.stl')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.blk')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.to')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.fouls')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t('estadisticasTab.val')}</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {(() => {
                const groupedBySeason = groupStatsBySeason(stats.seasonStats);
                const seasons = Object.keys(groupedBySeason).sort((a, b) => {
                  const yearA = parseInt(a.split('/')[1] || a.split('-')[1] || a);
                  const yearB = parseInt(b.split('/')[1] || b.split('-')[1] || b);
                  return yearB - yearA;
                });

                return seasons.map((seasonKey, idx) => {
                  const seasonData = groupedBySeason[seasonKey];
                  const seasonStats = calculateSeasonStatsUnified(seasonData.competitions, seasonData.seasonYear);

                  return (
                    <React.Fragment key={idx}>
                      {/* Ligne de la saison */}
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{seasonData.seasonYear}</TableCell>
                        <TableCell align="center">{seasonStats?.totalGames || '-'}</TableCell>
                        <TableCell align="center">{formatNumber(seasonStats?.avgMinutes)}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'points')}</TableCell>
                        <TableCell align="center">{formatPercentageWithRatio(seasonStats?.fgPercent, seasonStats?.totalFgMade, seasonStats?.totalFgAttempted)}</TableCell>
                        <TableCell align="center">{formatPercentageWithRatio(seasonStats?.threePPercent, seasonStats?.total3pMade, seasonStats?.total3pAttempted)}</TableCell>
                        <TableCell align="center">{formatPercentageWithRatio(seasonStats?.ftPercent, seasonStats?.totalFtMade, seasonStats?.totalFtAttempted)}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'reboundsOff')}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'reboundsDef')}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'rebounds')}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'assists')}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'steals')}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'blocks')}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'turnovers')}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'fouls')}</TableCell>
                        <TableCell align="center">{formatStatValue(seasonStats, 'ranking')}</TableCell>
                      </TableRow>

                      {/* Lignes des compétitions (si détails activés) */}
                      {showDetails && seasonData.competitions.map((comp, compIdx) => {
                        const compStats = getCompetitionStatsUnified(comp.stats, comp.name, seasonData.seasonYear);
                        const projKey = `${comp.name}-${seasonData.seasonYear}`;
                        const projData = projectionResults[projKey];
                        const projOpen = projectionOpen[projKey];
                        const projLoading = projectionLoading[projKey];

                        return (
                          <React.Fragment key={`${idx}-${compIdx}`}>
                            <TableRow sx={{ backgroundColor: '#fafafa' }}>
                              <TableCell sx={{ pl: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography component="span" sx={{ color: '#666', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                    → {comp.name}
                                    {comp.stats.team_name && (
                                      <>({comp.stats.team_name})</>
                                    )}
                                  </Typography>
                                  {comp.stats.team_logo && (
                                    <img
                                      src={comp.stats.team_logo}
                                      alt={comp.stats.team_name || 'Team logo'}
                                      style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }}
                                    />
                                  )}
                                  {destinationComp && (
                                    <Chip
                                      icon={projLoading ? <CircularProgress size={12} /> : <TrendingFlatIcon sx={{ fontSize: 14 }} />}
                                      label={projOpen ? destinationComp.name : t('estadisticasTab.projectTo', { league: destinationComp.name.split('-').pop() || destinationComp.name })}
                                      size="small"
                                      onClick={() => toggleProjection(comp.name, seasonData.seasonYear)}
                                      variant={projOpen ? 'filled' : 'outlined'}
                                      sx={{
                                        ml: 1,
                                        fontSize: '0.65rem',
                                        height: 22,
                                        cursor: 'pointer',
                                        borderColor: '#F25F29',
                                        color: projOpen ? '#fff' : '#F25F29',
                                        bgcolor: projOpen ? '#F25F29' : 'transparent',
                                        '&:hover': { bgcolor: projOpen ? '#d94f1e' : 'rgba(242,95,41,0.08)' },
                                        '& .MuiChip-icon': { color: projOpen ? '#fff' : '#F25F29' }
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">{compStats.totalGames}</TableCell>
                              <TableCell align="center">{formatNumber(compStats.avgMinutes)}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'points')}</TableCell>
                              <TableCell align="center">{formatPercentageWithRatio(compStats.fgPercent, compStats.totalFgMade, compStats.totalFgAttempted)}</TableCell>
                              <TableCell align="center">{formatPercentageWithRatio(compStats.threePPercent, compStats.total3pMade, compStats.total3pAttempted)}</TableCell>
                              <TableCell align="center">{formatPercentageWithRatio(compStats.ftPercent, compStats.totalFtMade, compStats.totalFtAttempted)}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'reboundsOff')}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'reboundsDef')}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'rebounds')}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'assists')}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'steals')}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'blocks')}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'turnovers')}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'fouls')}</TableCell>
                              <TableCell align="center">{formatStatValue(compStats, 'ranking')}</TableCell>
                            </TableRow>
                            {/* Projected stats row */}
                            {projOpen && projData && (() => {
                              if (projData.noData) {
                                return (
                                  <TableRow sx={{ backgroundColor: 'rgba(242, 95, 41, 0.06)' }}>
                                    <TableCell colSpan={16} sx={{ pl: 6 }}>
                                      <Typography sx={{ color: '#F25F29', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                        {t('estadisticasTab.noProjectionData')}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                              const projectedStats = applyProjection(compStats, projData.factors);
                              if (!projectedStats) return null;
                              return (
                                <TableRow sx={{ backgroundColor: 'rgba(242, 95, 41, 0.06)' }}>
                                  <TableCell sx={{ pl: 6 }}>
                                    <Typography sx={{ color: '#F25F29', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                      ↳ → {projData.destName}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled">
                                      ({projData.unique_players} jug.)
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center" sx={{ color: 'text.disabled' }}>—</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatNumber(projectedStats.avgMinutes)}</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'points')}</TableCell>
                                  <TableCell align="center" sx={{ color: 'text.disabled' }}>—</TableCell>
                                  <TableCell align="center" sx={{ color: 'text.disabled' }}>—</TableCell>
                                  <TableCell align="center" sx={{ color: 'text.disabled' }}>—</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'reboundsOff')}</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'reboundsDef')}</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'rebounds')}</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'assists')}</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'steals')}</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'blocks')}</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'turnovers')}</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'fouls')}</TableCell>
                                  <TableCell align="center" sx={{ color: '#F25F29', fontStyle: 'italic' }}>{formatStatValue(projectedStats, 'ranking')}</TableCell>
                                </TableRow>
                              );
                            })()}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Section Partidos */}
        {displayMode === 'games' && (
          <Box sx={{ mt: 2 }}>
            {(() => {
              const groupedBySeason = groupStatsBySeason(stats.seasonStats);
              const seasons = Object.keys(groupedBySeason).sort((a, b) => {
                const yearA = parseInt(a.split('/')[1] || a.split('-')[1] || a);
                const yearB = parseInt(b.split('/')[1] || b.split('-')[1] || b);
                return yearB - yearA;
              });

              return seasons.map((seasonKey, idx) => {
                const seasonData = groupedBySeason[seasonKey];

                return (
                  <Box key={idx} sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {seasonData.seasonYear}
                    </Typography>

                    {/* Liste des matchs par compétition */}
                    {seasonData.competitions.map((comp, compIdx) => {
                      // Filtrer les matchs de cette compétition et saison
                      const competitionMatches = stats.matchDetails.filter(match =>
                        match.competition_name === comp.name && match.season_year === seasonData.seasonYear
                      );

                      if (competitionMatches.length === 0) return null;

                      return (
                        <Box key={compIdx} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                              {comp.name}
                              {competitionMatches[0]?.team_name && (
                                <>({competitionMatches[0].team_name})</>
                              )}
                            </Typography>
                            {competitionMatches[0]?.team_logo && (
                              <img
                                src={competitionMatches[0].team_logo}
                                alt={competitionMatches[0].team_name || 'Team logo'}
                                style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }}
                              />
                            )}
                          </Box>

                          <TableContainer sx={{ maxHeight: 400 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.date')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.against')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.result')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.min')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.pts')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.t2')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.t3')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.tl')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.rebO')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.rebD')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.rebT')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.ast')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.stl')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.blk')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.to')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.fouls')}</TableCell>
                                  <TableCell sx={headerCellStyle}>{t('estadisticasTab.val')}</TableCell>
                                  <TableCell sx={headerCellStyle} align="center">{t('estadisticasTab.view')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {competitionMatches.map((match, matchIdx) => {
                                  const scoreDisplay = getScoreDisplay(match.result);
                                  return (
                                    <TableRow key={matchIdx} hover>
                                      <TableCell>{formatDate(match.match_date)}</TableCell>
                                      <TableCell>{match.opponent_team}</TableCell>
                                      <TableCell sx={{ fontWeight: 600, color: scoreDisplay.color }}>
                                        {scoreDisplay.display}
                                      </TableCell>
                                      <TableCell>{match.minutes}</TableCell>
                                      <TableCell>{match.points}</TableCell>
                                      <TableCell>{match.two_fg_percent === 0 ? '-' : match.two_fg_percent}</TableCell>
                                      <TableCell>{match.three_fg_percent === 0 ? '-' : match.three_fg_percent}</TableCell>
                                      <TableCell>{match.free_throw_percent === 0 ? '-' : match.free_throw_percent}</TableCell>
                                      <TableCell>{match.rebounds_off}</TableCell>
                                      <TableCell>{match.rebounds_def}</TableCell>
                                      <TableCell>{match.total_rebounds}</TableCell>
                                      <TableCell>{match.assists}</TableCell>
                                      <TableCell>{match.steals}</TableCell>
                                      <TableCell>{match.blocks}</TableCell>
                                      <TableCell>{match.turnovers}</TableCell>
                                      <TableCell>{match.personal_fouls}</TableCell>
                                      <TableCell>{match.ranking}</TableCell>
                                      <TableCell align="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => setSelectedMatch(match)}
                                          sx={{ color: '#1976d2' }}
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      );
                    })}
                  </Box>
                );
              });
            })()}
          </Box>
        )}
      </Paper>

      {/* DIALOG PARA DETALLES DEL PARTIDO */}
      <Dialog
        open={!!selectedMatch}
        onClose={() => setSelectedMatch(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedMatch && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box>
                <Typography variant="h6">{t('estadisticasTab.matchDetails')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {formatDate(selectedMatch.match_date)}
                  </Typography>
                  <Typography variant="subtitle2">
                    vs {selectedMatch.opponent_team}
                  </Typography>
                  {selectedMatch.result && selectedMatch.result.includes('-') && (
                    <Chip 
                      label={selectedMatch.result} 
                      size="small"
                      sx={{ 
                        fontWeight: 600,
                        color: getScoreDisplay(selectedMatch.result).color 
                      }}
                    />
                  )}
                </Box>
              </Box>
            </DialogTitle>
            
            <DialogContent dividers>
              <Grid container spacing={2}>
                {/* Estadísticas Básicas */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t('estadisticasTab.basicStats')}
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.minutes')}</Typography>
                        <Typography variant="h6">{selectedMatch.minutes || '-'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.points')}</Typography>
                        <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700 }}>{selectedMatch.points || '-'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.rating')}</Typography>
                        <Typography variant="h6">{selectedMatch.ranking || '-'}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Tiros */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t('estadisticasTab.shootingPercentages')}
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">{t('estadisticasTab.twoPointers')}</Typography>
                      <Chip label={selectedMatch.two_fg_percent === 0 ? '-' : (selectedMatch.two_fg_percent || '-')} size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">{t('estadisticasTab.threePointers')}</Typography>
                      <Chip label={selectedMatch.three_fg_percent === 0 ? '-' : (selectedMatch.three_fg_percent || '-')} size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">{t('estadisticasTab.freeThrows')}</Typography>
                      <Chip label={selectedMatch.free_throw_percent === 0 ? '-' : (selectedMatch.free_throw_percent || '-')} size="small" />
                    </Box>
                  </Stack>
                </Grid>

                {/* Rebotes */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t('estadisticasTab.rebounds')}
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.offensive')}</Typography>
                        <Typography variant="h6">{selectedMatch.rebounds_off || '0'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.defensive')}</Typography>
                        <Typography variant="h6">{selectedMatch.rebounds_def || '0'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f3e5f5' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.total')}</Typography>
                        <Typography variant="h6" sx={{ color: '#9c27b0', fontWeight: 700 }}>{selectedMatch.total_rebounds || '0'}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Otras Estadísticas */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t('estadisticasTab.otherStats')}
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={3}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.assists')}</Typography>
                        <Typography variant="h6">{selectedMatch.assists || '0'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.steals')}</Typography>
                        <Typography variant="h6">{selectedMatch.steals || '0'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.blocks')}</Typography>
                        <Typography variant="h6">{selectedMatch.blocks || '0'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary">{t('estadisticasTab.turnovers')}</Typography>
                        <Typography variant="h6">{selectedMatch.turnovers || '0'}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Faltas */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#fafafa', borderRadius: 1 }}>
                    <Typography variant="body2">{t('estadisticasTab.personalFouls')}</Typography>
                    <Chip 
                      label={selectedMatch.personal_fouls || '0'} 
                      color={selectedMatch.personal_fouls >= 4 ? 'error' : 'default'}
                    />
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={() => setSelectedMatch(null)}>{t('estadisticasTab.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* COMPARACIÓN RADAR - INTEGRADO EN LA PÁGINA */}
      {playerInfo && currentSeasonStats && (
        <Paper elevation={0} sx={{ mb: 2, p: { xs: 1, sm: 1.5 }, border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              {t('estadisticasTab.comparison')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                variant={radarSeasonType === 'current' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleRadarSeasonTypeChange('current')}
                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1 }}
              >
                {t('estadisticasTab.currentSeason')}
              </Button>
              {hasMultipleSeasons && careerStats && (
                <Button
                  variant={radarSeasonType === 'career' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleRadarSeasonTypeChange('career')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1 }}
                >
                  {t('estadisticasTab.career')}
                </Button>
              )}
            </Box>
          </Box>

          {/* Controles de filtrage */}
          <Box sx={{ mb: 1.5 }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: isXs ? 'column' : 'row' }}>
                {/* Selector de posiciones */}
                <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                  <InputLabel>{t('estadisticasTab.positionsToCompare')}</InputLabel>
                  <Select
                    multiple
                    value={selectedPositions}
                    onChange={handlePositionChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" sx={{ height: 20 }} />
                        ))}
                      </Box>
                    )}
                  >
                    {positions.map((position) => (
                      <MenuItem key={position} value={position} dense>
                        {position}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Filtro de edad */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeAgeFilter}
                        onChange={(e) => setIncludeAgeFilter(e.target.checked)}
                        size="small"
                      />
                    }
                    label={t('estadisticasTab.filterAge')}
                    sx={{ m: 0 }}
                  />
                  {includeAgeFilter && (
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={ageRange}
                        onChange={(e) => setAgeRange(e.target.value)}
                      >
                        <MenuItem value={1} dense>±1 año</MenuItem>
                        <MenuItem value={2} dense>±2 años</MenuItem>
                        <MenuItem value={3} dense>±3 años</MenuItem>
                        <MenuItem value={5} dense>±5 años</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Box>

              {/* Información de comparación */}
              {radarComparisonData && (
                <Alert
                  severity="info"
                  icon={<InfoOutlinedIcon fontSize="small" />}
                  sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.875rem' } }}
                >
                  {t('estadisticasTab.comparing', { count: radarComparisonData.playerCount })}
                  {selectedPositions.length > 0 && ` ${t('estadisticasTab.inPosition', { positions: selectedPositions.join(', ') })}`}
                  {includeAgeFilter && playerInfo.age && ` ${t('estadisticasTab.ageRange', { min: playerInfo.age - ageRange, max: playerInfo.age + ageRange })}`}
                </Alert>
              )}
            </Stack>
          </Box>

          {/* Gráfico Radar */}
          {radarLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : radarData.length > 0 ? (
            <Box sx={{ width: '100%', height: isXs ? 280 : 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
                  <PolarGrid stroke="#e0e0e0" strokeDasharray="3 3" />
                  <PolarAngleAxis
                    dataKey="stat"
                    tick={{ fontSize: isXs ? 9 : 11 }}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 100]}
                    tick={{ fontSize: 9 }}
                  />
                  <Radar
                    name={playerInfo.name || 'Jugador'}
                    dataKey={playerInfo.name || 'Jugador'}
                    stroke="#1976d2"
                    fill="#1976d2"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Radar
                    name="Promedio Liga"
                    dataKey="Promedio Liga"
                    stroke="#ff9800"
                    fill="#ff9800"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                  <Legend wrapperStyle={{ fontSize: '0.875rem' }} iconType="circle" />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              {t('estadisticasTab.selectPosition')}
            </Alert>
          )}
        </Paper>
      )}

      </>
      )}

      {/* Tab 1: Joueurs Similaires */}
      {mainTab === 1 && (
        <SimilarStatsPlayersTab playerId={playerId} />
      )}
    </Box>
  );
};

export default EstadisticasTab;